import { decorateChatItems, type DecorationOptions } from './decorate';
import { compareSortOrders } from './ids';
import type { ChatItem, ClientRequestId, MessageId, SortOrder, SyncCursor } from './types';

/** Pagination and sync cursors for the currently loaded contiguous window. */
export interface ChatFeedCursors {
  readonly olderCursor?: SortOrder;
  readonly newerCursor?: SortOrder;
  readonly syncCursor?: SyncCursor;
}

/** Immutable feed store view; items are a shallow copy and epoch increments on mutations. */
export interface ChatFeedStoreSnapshot extends ChatFeedCursors {
  readonly items: ChatItem[];
  readonly epoch: number;
}

/** Store mutation result with previous/next arrays and newly inserted rows. */
export interface ChatFeedStoreMutation {
  readonly previous: ChatItem[];
  readonly next: ChatItem[];
  readonly inserted: ChatItem[];
}

/** Adjacent-page mutation that reports whether the final window was safely bounded. */
export interface ChatFeedStoreBoundedMutation extends ChatFeedStoreMutation {
  readonly pruned: boolean;
}

type ChatFeedPruneDirection = 'older' | 'newer';

/** In-memory ordered chat row store; callers receive snapshots, not mutable internals. */
export class ChatFeedStore {
  private items: ChatItem[] = [];
  private olderCursor?: SortOrder;
  private newerCursor?: SortOrder;
  private syncCursor?: SyncCursor;
  private epoch = 0;

  /** Creates an empty store and applies decoration options to every emitted row array. */
  constructor(private readonly decorationOptions: DecorationOptions = {}) {}

  /** Returns a shallow-copied item snapshot plus cursors and epoch. */
  snapshot(): ChatFeedStoreSnapshot {
    return {
      items: [...this.items],
      olderCursor: this.olderCursor,
      newerCursor: this.newerCursor,
      syncCursor: this.syncCursor,
      epoch: this.epoch
    };
  }

  /** Durable id for the first loaded server row, or undefined for an empty/optimistic-only window. */
  firstLoadedId(): MessageId | undefined {
    return this.items.find((item) => item.id !== undefined)?.id;
  }

  /** Durable id for the last loaded server row, or undefined for an empty/optimistic-only window. */
  lastLoadedId(): MessageId | undefined {
    for (let index = this.items.length - 1; index >= 0; index -= 1) {
      const id = this.items[index]?.id;
      if (id) return id;
    }
    return undefined;
  }

  /** Replaces the full window, dedupes by itemKey, updates cursors, and returns all next rows as inserted. */
  replaceWindow(items: ChatItem[], cursors: ChatFeedCursors): ChatFeedStoreMutation {
    const previous = this.items;
    const next = this.decorate(dedupeItems(items));
    this.items = next;
    this.setCursors(cursors);
    this.epoch += 1;
    return { previous, next, inserted: next };
  }

  /** Reconciles authoritative rows with active identity and carries only unresolved local sends. */
  reconciledLatestItems(
    authoritativeItems: readonly ChatItem[],
    mode: 'replace' | 'merge',
    removedIds: readonly MessageId[] = []
  ): ChatItem[] {
    const active = this.items;
    const localRows = active.filter(isUnresolvedLocalRow);
    const activeById = new Map(active.flatMap((item) => item.id ? [[item.id, item] as const] : []));
    const localByClientRequestId = new Map(localRows.map((item) => [item.clientRequestId, item]));
    const reconciled = authoritativeItems.map((item) => {
      const matched = (item.id ? activeById.get(item.id) : undefined) ??
        (item.clientRequestId ? localByClientRequestId.get(item.clientRequestId) : undefined);
      return matched ? mergeActiveIdentity(matched, item) : item;
    });
    const acknowledgedClientRequestIds = new Set(reconciled.flatMap((item) =>
      item.clientRequestId ? [item.clientRequestId] : []
    ));
    const unresolved = localRows.filter((item) => !acknowledgedClientRequestIds.has(item.clientRequestId));
    if (mode === 'replace') return dedupeItems([...reconciled, ...unresolved]);

    const removed = new Set(removedIds);
    const authoritativeById = new Map(reconciled.flatMap((item) => item.id ? [[item.id, item] as const] : []));
    const authoritativeByClientRequestId = new Map(reconciled.flatMap((item) =>
      item.clientRequestId ? [[item.clientRequestId, item] as const] : []
    ));
    const representedIds = new Set<MessageId>();
    const serverRows = active.flatMap((item) => {
      if (!item.id || removed.has(item.id)) return [];
      const next = authoritativeById.get(item.id) ??
        (item.clientRequestId ? authoritativeByClientRequestId.get(item.clientRequestId) : undefined);
      const resolved = next ? mergeActiveIdentity(item, next) : item;
      if (resolved.id) representedIds.add(resolved.id);
      return [resolved];
    });
    const newestLoadedSortOrder = serverRows.at(-1)?.sortOrder;
    const additions = reconciled.filter((item) => (
      item.id &&
      !representedIds.has(item.id) &&
      (!newestLoadedSortOrder || compareSortOrders(item.sortOrder!, newestLoadedSortOrder) > 0)
    ));
    return dedupeItems([...serverRows, ...additions, ...unresolved]);
  }

  /** Merges an older page before current rows and updates the older cursor. */
  prependOlder(items: ChatItem[], olderCursor: SortOrder | undefined): ChatFeedStoreMutation {
    const mutation = this.mergeAdjacentPage(items, 'older');
    this.olderCursor = olderCursor;
    return mutation;
  }

  /** Merges a newer page after current rows and updates the newer cursor. */
  appendNewer(items: ChatItem[], newerCursor: SortOrder | undefined): ChatFeedStoreMutation {
    const mutation = this.mergeAdjacentPage(items, 'newer');
    this.newerCursor = newerCursor;
    return mutation;
  }

  /** Merges an older page and decorates only the final safely bounded window. */
  prependOlderBounded(
    items: ChatItem[],
    olderCursor: SortOrder | undefined,
    maxServerRows: number,
    protectedKeys: ReadonlySet<string>
  ): ChatFeedStoreBoundedMutation {
    return this.mergeAdjacentPageBounded(items, olderCursor, 'older', maxServerRows, protectedKeys);
  }

  /** Merges a newer page and decorates only the final safely bounded window. */
  appendNewerBounded(
    items: ChatItem[],
    newerCursor: SortOrder | undefined,
    maxServerRows: number,
    protectedKeys: ReadonlySet<string>
  ): ChatFeedStoreBoundedMutation {
    return this.mergeAdjacentPageBounded(items, newerCursor, 'newer', maxServerRows, protectedKeys);
  }

  /** Reopens older pagination at a loaded boundary after a premature terminal claim. */
  reopenOlder(cursor: SortOrder): boolean {
    if (this.olderCursor) return false;
    this.olderCursor = cursor;
    this.epoch += 1;
    return true;
  }

  private mergeAdjacentPage(items: ChatItem[], side: 'older' | 'newer'): ChatFeedStoreMutation {
    const previous = this.items;
    const previousKeys = itemKeySet(previous);
    const merged = reuseActiveItems(items, previous);
    const combined = dedupeItems(side === 'older' ? [...merged, ...previous] : [...previous, ...merged]);
    // Late socket deliveries and resync creates can arrive older than the loaded
    // tail; blind concatenation would display them out of chronological order.
    const next = this.decorate(sortByServerOrder(combined));
    const inserted = next.filter((item) => !previousKeys.has(item.itemKey));
    this.items = next;
    this.epoch += 1;
    return { previous, next, inserted };
  }

  private mergeAdjacentPageBounded(
    items: ChatItem[],
    cursor: SortOrder | undefined,
    side: ChatFeedPruneDirection,
    maxServerRows: number,
    protectedKeys: ReadonlySet<string>
  ): ChatFeedStoreBoundedMutation {
    const previous = this.items;
    const previousKeys = itemKeySet(previous);
    const merged = reuseActiveItems(items, previous);
    const combined = sortByServerOrder(dedupeItems(side === 'older' ? [...merged, ...previous] : [...previous, ...merged]));
    if (side === 'older') {
      this.olderCursor = cursor;
    } else {
      this.newerCursor = cursor;
    }

    // Pruning bounds insertion growth only: a merge that added no server rows
    // (detachLiveTail, cached end seals, empty terminal pages, duplicate pages)
    // cannot have grown the window and must never restructure it as a side
    // effect of a cursor write.
    const grewServerRows = countServerRows(combined) > countServerRows(previous);
    const pruned = grewServerRows
      ? safelyPrunedItems(combined, side, maxServerRows, protectedKeys)
      : undefined;
    const next = this.decorate(pruned?.items ?? combined);
    const inserted = next.filter((item) => !previousKeys.has(item.itemKey));
    this.items = next;
    if (pruned && side === 'older') {
      this.newerCursor = pruned.boundaryCursor;
    } else if (pruned) {
      this.olderCursor = pruned.boundaryCursor;
    }
    this.epoch += 1;
    return { previous, next, inserted, pruned: pruned !== undefined };
  }

  /** Appends a live row and clears the newer cursor because the window is live-contiguous. */
  appendLive(message: ChatItem): ChatFeedStoreMutation {
    return this.appendNewer([message], undefined);
  }

  /** Appends an optimistic local row without changing pagination cursors. */
  appendOptimistic(message: ChatItem): ChatFeedStoreMutation {
    const previous = this.items;
    const previousKeys = itemKeySet(previous);
    const next = this.decorate(dedupeItems([...previous, message]));
    const inserted = next.filter((item) => !previousKeys.has(item.itemKey));
    this.items = next;
    this.epoch += 1;
    return { previous, next, inserted };
  }

  /** Replaces a local pending row by clientRequestId; returns undefined when absent. */
  reconcileSendAck(clientRequestId: ClientRequestId, item: ChatItem): ChatFeedStoreMutation | undefined {
    const index = this.items.findIndex((candidate) => candidate.clientRequestId === clientRequestId);
    if (index < 0) return undefined;

    const previous = this.items;
    const nextItem = mergeActiveIdentity(previous[index], item);
    const next = this.decorate(previous.map((candidate, candidateIndex) => (
      candidateIndex === index ? nextItem : candidate
    )));
    this.items = next;
    this.epoch += 1;
    return { previous, next, inserted: [] };
  }

  /** Marks a sending row failed by clientRequestId; returns undefined when absent or already settled. */
  markSendFailed(clientRequestId: ClientRequestId): ChatFeedStoreMutation | undefined {
    const index = this.items.findIndex((candidate) => candidate.clientRequestId === clientRequestId);
    const current = this.items[index];
    if (!current || current.status !== 'sending') return undefined;

    const previous = this.items;
    const failed = { ...current, status: 'failed' as const };
    const next = this.decorate(previous.map((candidate, candidateIndex) => (
      candidateIndex === index ? failed : candidate
    )));
    this.items = next;
    this.epoch += 1;
    return { previous, next, inserted: [] };
  }

  /** Updates one loaded row by durable id or itemKey; returns undefined when absent. */
  updateLoadedItem(item: ChatItem): ChatFeedStoreMutation | undefined {
    const index = this.findLoadedIndex(item);
    if (index < 0) return undefined;

    const previous = this.items;
    const nextItem = mergeActiveIdentity(previous[index], item);
    const next = this.decorate(previous.map((candidate, candidateIndex) => (
      candidateIndex === index ? nextItem : candidate
    )));
    this.items = next;
    this.epoch += 1;
    return { previous, next, inserted: [] };
  }

  /** Updates loaded rows in batch; returns undefined when none match. */
  updateLoadedItems(items: readonly ChatItem[]): ChatFeedStoreMutation | undefined {
    if (items.length === 0) return undefined;

    const previous = this.items;
    const replacements = new Map<number, ChatItem>();
    for (const item of items) {
      const index = this.findLoadedIndex(item);
      if (index >= 0) {
        replacements.set(index, mergeActiveIdentity(previous[index], item));
      }
    }
    if (replacements.size === 0) return undefined;

    const next = this.decorate(previous.map((candidate, index) => replacements.get(index) ?? candidate));
    this.items = next;
    this.epoch += 1;
    return { previous, next, inserted: [] };
  }

  /** Removes server-backed rows by durable id or sort order; optimistic rows are retained. */
  removeLoadedItems(ids: readonly MessageId[], sortOrders: readonly SortOrder[] = []): ChatFeedStoreMutation | undefined {
    if (ids.length === 0 && sortOrders.length === 0) return undefined;

    const idsToRemove = new Set(ids);
    const sortOrdersToRemove = new Set(sortOrders);
    const previous = this.items;
    const nextItems = previous.filter((item) => (
      (!item.id || !idsToRemove.has(item.id)) &&
      (!item.sortOrder || !sortOrdersToRemove.has(item.sortOrder))
    ));
    if (nextItems.length === previous.length) return undefined;

    const next = this.decorate(nextItems);
    this.items = next;
    this.epoch += 1;
    return { previous, next, inserted: [] };
  }

  private findLoadedIndex(item: ChatItem): number {
    if (item.id) {
      const idIndex = this.items.findIndex((candidate) => candidate.id === item.id);
      if (idIndex >= 0) return idIndex;
    }
    return this.items.findIndex((candidate) => candidate.itemKey === item.itemKey);
  }

  private setCursors(cursors: ChatFeedCursors): void {
    this.olderCursor = cursors.olderCursor;
    this.newerCursor = cursors.newerCursor;
    this.syncCursor = cursors.syncCursor;
  }

  private decorate(items: ChatItem[]): ChatItem[] {
    return decorateChatItems(items, this.decorationOptions);
  }
}

function itemKeySet(items: ChatItem[]): Set<string> {
  return new Set(items.map((item) => item.itemKey));
}

// Stable: pending local rows (no sortOrder) keep their relative order at the tail.
function sortByServerOrder(items: ChatItem[]): ChatItem[] {
  return [...items].sort((left, right) => {
    if (left.sortOrder === undefined || right.sortOrder === undefined) {
      return left.sortOrder === right.sortOrder ? 0 : left.sortOrder === undefined ? 1 : -1;
    }
    return compareSortOrders(left.sortOrder, right.sortOrder);
  });
}

function dedupeItems(items: ChatItem[]): ChatItem[] {
  const seen = new Set<string>();
  const next: ChatItem[] = [];
  for (const item of items) {
    if (seen.has(item.itemKey)) continue;
    seen.add(item.itemKey);
    next.push(item);
  }
  return next;
}

function reuseActiveItems(items: ChatItem[], activeItems: ChatItem[]): ChatItem[] {
  const activeById = new Map<MessageId, ChatItem>();
  for (const item of activeItems) {
    if (item.id) activeById.set(item.id, item);
  }

  return items.map((item) => {
    if (!item.id) return item;
    return activeById.get(item.id) ?? item;
  });
}

function mergeActiveIdentity(active: ChatItem, incoming: ChatItem): ChatItem {
  const clientRequestId = active.clientRequestId ?? incoming.clientRequestId;
  // SAFETY: incoming is already a ChatItem; itemKey/clientRequestId do not affect status/deleted discriminants.
  return {
    ...incoming,
    itemKey: active.itemKey,
    ...(clientRequestId === undefined ? {} : { clientRequestId })
  } as ChatItem;
}

function isUnresolvedLocalRow(item: ChatItem): item is Extract<ChatItem, { status: 'sending' | 'failed' }> {
  return item.status === 'sending' || item.status === 'failed';
}

function countServerRows(items: readonly ChatItem[]): number {
  return items.reduce((count, item) => count + (isServerBackedRow(item) ? 1 : 0), 0);
}

function isServerBackedRow(item: ChatItem): boolean {
  return item.id !== undefined && item.sortOrder !== undefined;
}

function safelyPrunedItems(
  items: readonly ChatItem[],
  direction: ChatFeedPruneDirection,
  maxServerRows: number,
  protectedKeys: ReadonlySet<string>
): { readonly boundaryCursor: SortOrder; readonly items: ChatItem[] } | undefined {
  const serverRows = items.filter(isServerBackedRow);
  if (serverRows.length <= maxServerRows) return undefined;

  const retainedServerRows = direction === 'older'
    ? serverRows.slice(0, maxServerRows)
    : serverRows.slice(-maxServerRows);
  const retainedKeys = itemKeySet(retainedServerRows);
  const removedServerRows = serverRows.filter((item) => !retainedKeys.has(item.itemKey));
  if (removedServerRows.some((item) => protectedKeys.has(item.itemKey))) return undefined;
  if (direction === 'older' && items.some(isUnresolvedLocalRow)) return undefined;

  const boundaryCursor = direction === 'older'
    ? retainedServerRows.at(-1)?.sortOrder
    : retainedServerRows[0]?.sortOrder;
  if (!boundaryCursor) return undefined;
  return {
    boundaryCursor,
    items: items.filter((item) => !isServerBackedRow(item) || retainedKeys.has(item.itemKey))
  };
}
